import React from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent, TooltipComponentOption } from 'echarts/components';
import { PieChart, PieSeriesOption } from 'echarts/charts';
import { ECharts, EChartsCoreOption } from 'echarts/core';

echarts.use([TooltipComponent, PieChart]);

const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  addAlphaToHexColor: (hex: string, alpha: number) => string,
  isDark: boolean,
  data: { value: number; name: string }[]
): EChartsCoreOption => ({
  color: [
    getThemeColor('success-light'),
    // eslint-disable-next-line
    !isDark ? getThemeColor('primary-lighter') : getThemeColor('primary-lighter'),
    !isDark ? getThemeColor('primary-light') : getThemeColor('primary-light'),
    // eslint-disable-next-line
    !isDark ? getThemeColor('primary') : addAlphaToHexColor(getThemeColor('primary-dark'), 0.8),
    !isDark ? getThemeColor('primary-darker') : getThemeColor('primary-darker')
  ],
  tooltip: {
    show: false,
    trigger: 'item',
    // eslint-disable-next-line
    backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
    textStyle: {
      color: !isDark ? '#202020' : '#ccc',
      fontFamily: 'Nunito Sans',
      fontSize: 14
    }
  } as TooltipComponentOption,
  responsive: true,
  maintainAspectRatio: false,
  series: [
    {
      name: 'Calls',
      type: 'pie',
      radius: ['78%', '93%'],
      startAngle: 90,
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 5,
        // eslint-disable-next-line
        borderColor: !isDark ? 'rgba(240, 240, 240, 0.8)' : 'rgba(49, 55, 74, 0.8)',
        borderWidth: 1
      },
      label: {
        show: true,
        position: 'center',
        formatter: '{x|{d}%} \n {y|{b}}',
        rich: {
          x: {
            fontSize: 18,
            fontWeight: 800,
            color: getThemeColor('tertiary-color'),
            padding: [10, 0, 0, 5]
          },
          y: {
            fontSize: 12,
            color: getThemeColor('tertiary-color'),
            fontWeight: 500,
            padding: [0, 5, 0, 0]
          }
        }
      },
      emphasis: {
        label: {
          show: true,
          fontSize: '16',
          fontWeight: 'bold',
          // eslint-disable-next-line
          backgroundColor: !isDark ? 'rgba(245, 247, 250, 1)' : 'rgba(15, 17, 26, 1)',
          borderRadius: 5,
          padding: 5
        }
      },
      labelLine: {
        show: false
      },
      data
    } as PieSeriesOption
  ],
  grid: {
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    containLabel: false
  }
});

interface DialingSuccessChartProps {
  data: { value: number; name: string }[];
}

const DialingSuccessChart: React.FC<DialingSuccessChartProps> = ({ data }) => {
  const {
    getThemeColor,
    addAlphaToHexColor,
    config: { isDark }
  } = useAppContext();

  const onChartReady = (chart: ECharts) => {
    // Resaltar "Answer" por defecto
    chart.dispatchAction({
      type: 'highlight',
      seriesIndex: 0,
      dataIndex: 0
    });
    chart.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: 0
    });
    // eslint-disable-next-line
    chart.on('mouseover', (params: any) => {
      if (params.name !== 'Answer') {
        chart.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: 0
        });
        chart.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: params.dataIndex
        });
        chart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: params.dataIndex
        });
      }
    });
    // eslint-disable-next-line
    chart.on('mouseout', (params: any) => {
      chart.dispatchAction({
        type: 'downplay',
        seriesIndex: 0,
        dataIndex: params.dataIndex
      });
      chart.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: 0
      });
      chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: 0
      });
    });
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      // eslint-disable-next-line
      option={getDefaultOptions(getThemeColor, addAlphaToHexColor, isDark, data)}
      style={{ height: '180px', width: '100%' }}
      onChartReady={onChartReady}
    />
  );
};

export default DialingSuccessChart;
